/**
 * Birdie GRID board — pure geometry. No DOM, no React, and no RUNTIME imports:
 * the single `import type` below is erased by the compiler, so this module still
 * pulls nothing into the bundle and its tests still need no mocks. It is there
 * so `cardBoxPx`'s capsule state cannot drift from `capsuleState.ts`'s union
 * (B16) — a second hand-written copy of those three names is exactly the kind of
 * duplicate that goes stale.
 *
 * Everything is expressed against the PICTURE BOX (`frame`), never the viewport
 * and never `.marbles-race__bottom` (which is CSS-transform-scaled by
 * `useViewportScale`, so a % of it is not a % of the picture). Since B30 that
 * box is DERIVED — `houseFrame` contain-fits the house 16:9 inside the stage —
 * rather than measured off the clip, so it is a function of the window alone.
 *
 * ONE LAYOUT, ONE TRANSFORM (B14, owner 2026-09-07: "we've used ui scale so
 * everything basically stays the same when we make a window smaller, just scale
 * it down, no breakpoints so it looks consistent in iframes"). The board is laid
 * out ONCE, in the LAYOUT FRAME (`layoutFrame(frame)`), and the whole thing is
 * then CSS-scaled onto the picture box by `frameScale(frame)` — one element,
 * one transform, no child that knows the window size.
 *
 * The layout frame is the picture divided by that scale, so scaling it
 * back up by `k` lands it exactly on the picture. Below the reference size it IS
 * `REFERENCE_FRAME` (a 990-wide picture over k 0.7734 is 1280 again); at and
 * above it, where `k` is capped at 1, it is the measured picture itself. That
 * cap is B21's "the board stops growing with the window", and putting it on the
 * LAYOUT rather than on the transform is what keeps the board registered to the
 * picture it is drawn on: the chrome the columns are resolved against — the chip
 * bar, the ball channel, the icon rail, the social feed — is a FRACTION of the
 * frame, so a board that stopped at 1280 in the corner of a 1846-wide picture
 * would hang 300 px above its own chip bar and put the count bars on the putt's
 * path. Sizes still stop: inside the layout frame `k` is 1, so a tile is the
 * spec's 112 x 60 CSS px on any picture bigger than the reference.
 *
 * Consequences for callers: pass `boardGeometry` a LAYOUT frame (production uses
 * `boardGeometryFor(frame)`, which memoises one), every number it returns is in
 * that frame's px, and the picture box is used for two things only — the
 * overlay box's `left`/`top`, and `k`.
 *
 * ── B20: TWO COLUMNS, NO ARCS ─────────────────────────────────────────
 * Everything elliptical is gone (owner sign-off 2026-09-08 on the grid mockup).
 * There are no arcs, no hairline countdown (the capsule ring is the only
 * countdown now), no tile angles and no captions. The board is two columns:
 *
 *   left  `exact`  six 112 x 60 tiles in a 2 x 3 grid, row-major, in the
 *                  order HMM MHM MMH HHM HMH MHH
 *   right `count`  four 176 x 42 bars stacked, IN0 (top) -> IN3 (bottom)
 *
 * each under a header line ("Exact order" / "Any order", the only text on the
 * board). Sizes are fixed reference px scaled by `k`, exactly like the arcs'
 * tiles were — a column NEVER shrinks to fit, it MOVES.
 *
 * ── B21: SMALLER, AND ANCHORED TO THE BOTTOM ──────────────────────────
 * Owner on the live grid, 2026-09-08: "it all looks a bit too large, e.g. the
 * multipliers on the betting options … think it can all be a bit smaller …
 * i think the betting options can be moved further down". So the tiles are the
 * chip panel's own type step (a 13-px price, `chip-placement.scss`'s
 * `&__opt-pays-value`), `frameScale` no longer grows past 1 (see the layout
 * frame above — the cap is on the SIZES, not on the registration), and the
 * columns hang off the BOTTOM of the stage rather than off the mockup's top.
 *
 * That is the whole placement rule and it is why `resolveColumn` below exists.
 * Both columns start with their tops aligned and the TALLER block's bottom
 * `bottomGap` above the chip bar, and each is then pushed INWARD (the left
 * column right, the right column left) or UP by the smallest step that clears
 * the next protected zone, never resized. Down is not an escape any more —
 * there is nothing below the board but the chip bar. On the reference frame
 * the left column loses its target x to the social feed and the right column
 * loses its to the icon rail; neither has to move vertically, because the
 * capsule's two boxes are 150 px above where the grid now starts.
 *
 * Two things the board draws are NOT tile boxes and are modelled here anyway,
 * because a layout that ignores them puts them under the widget's own chrome:
 * the settled `<WinPill>`, which grows upward out of a tile's top edge and is
 * paid for by `pillBand` above the first row, and the lightning seal / chip
 * pile, which hang off a tile's corners and are paid for by `tileBleed` on the
 * side the block is pushed from.
 *
 * The numbers in BIRDIE_BOARD_SPEC are tuning, not contract — the tests assert
 * the invariants (no tile in a protected zone, no two boxes overlapping, the
 * right column inside 93 % of the width, the left column clear of the social
 * feed, both columns clear of the capsule in every state, both column bottoms
 * above the chip bar, the taller block's bottom ON the anchor, the pill band
 * above every row-0 tile, the bled tile boxes still clear of every zone, sizes
 * scaling exactly by k) and those must keep passing after any retune.
 */

import type { CapsuleLayout } from './capsuleState'

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

export interface Box {
  left: number
  top: number
  width: number
  height: number
}

/** The two markets, which are also the two columns. */
export type BoardColumn = 'count' | 'exact'
/** Which frame edge a column is measured from, and which way it is pushed. */
export type ColumnSide = 'left' | 'right'

export interface ColumnSpec {
  /** Frame edge the column hangs off, and the direction it is pushed inward. */
  side: ColumnSide
  /** Grid columns inside the column block (2 for exact, 1 for count). */
  gridColumns: number
  /** Tile width in REFERENCE px. */
  tileWidth: number
  /** Tile height in REFERENCE px. */
  tileHeight: number
  /** Gap between grid columns, REFERENCE px. Unused when `gridColumns` is 1. */
  columnGap: number
  /** Gap between rows, REFERENCE px. */
  rowGap: number
  /**
   * The mockup's target, as an INSET from the column's own frame edge: the
   * left edge when `side` is 'left', the right edge when `side` is 'right'.
   * REFERENCE px, scaled by `k` like every other fixed size.
   *
   * An inset rather than an absolute x (the review of the first B21 build): the
   * layout frame is the picture on anything wider than the reference, so a
   * right-hand column pinned to x 1249.3 would sit in the middle of it instead
   * of hanging off the icon rail. Both columns use the same 30.7, which is what
   * makes the board symmetrical about the green before the resolver moves it.
   *
   * There is no target TOP any more (B21): both columns are anchored to the
   * bottom of the stage by `bottomGap`, and the resolver only ever lifts them.
   */
  targetInset: number
  /**
   * The inset while BETTING IS OPEN (owner, 2026-09-08: "have them even closer
   * and then when betting closes we animate them out"). No ball is rolling, so
   * the channel keep-out is off and the columns come in as far as the hole's
   * own keep-out and the chrome allow; on `race_started` they slide out to
   * `targetInset`, clearing the putt's path. Same anchor and rows either way.
   */
  bettingInset: number
  /** Option keys, row-major. Index 0 is the top-left cell. */
  keys: readonly string[]
}

/** A rectangle of existing widget chrome, as fractions of the frame. */
export interface ChromeZone {
  left: number
  right: number
  top: number
  bottom: number
}

export interface BoardSpec {
  /** Hole centre as fractions of the frame width / height. */
  hole: { x: number; y: number }
  /** Ball channel as fractions of the frame width. */
  channel: { left: number; right: number }
  /** Extra clearance outside the channel, fraction of the frame width. */
  channelMargin: number
  /** Keep-out at the frame's left/right edge, fraction of the frame width. */
  edgePad: number
  /**
   * Top of the chip bar, fraction of the frame height.
   *
   * B31 re-measured it against the SHARED strip the board now renders: in a
   * static 1280x720 harness over the compiled stylesheets, `.chip-panel__bar`
   * is 48 px tall with the auto-bet toggle and 48 px without it, and the
   * `.birdie-bar` it replaced was 48 px too — one 44-px control row plus the
   * bar's 2 px of padding, in all three cases. The desktop keep-out is
   * unchanged; only the phone's bar changed height.
   */
  chipBarTop: number
  /**
   * The board's bottom anchor, REFERENCE px above `chipBarTop` (B21, owner:
   * "the betting options can be moved further down").
   *
   * The two columns are laid out from the BOTTOM: their tops are aligned and
   * the TALLER block's bottom edge sits exactly this far above the chip bar,
   * which is why the grid now starts around y 447 instead of y 333. Twelve px
   * is the gap the mockup leaves between the last row and the bar — close
   * enough to read as one stack, far enough that a placed chip's 9-px overhang
   * (`tileBleed`) never touches it.
   */
  bottomGap: number
  /** Reserved band for the engine's burned-in putt rings (fractions). */
  ringBand: ChromeZone
  /** Hole keep-out radius, fraction of the frame HEIGHT. */
  holeRadius: number
  /** Clearance a column keeps from every zone it is pushed off, REFERENCE px. */
  zoneGap: number
  /** The header line above each column. */
  header: {
    /**
     * REFERENCE px. The header is an inline row whose tallest item is its icon
     * (`.birdie-board__head-icon`), so that — and not the 8.5-px label — is the
     * line's height. B21 stepped both down: a 15-px icon, an 8.5-px word.
     */
    height: number
    /** `margin-bottom: .05cqw` in the mockup: the gap down to the first row. */
    gap: number
  }
  /**
   * REFERENCE px reserved between the header line and the FIRST tile row for
   * the settled `<WinPill>` — and, since the row gaps were raised onto it, the
   * same number every row gap is set to.
   *
   * The pill is anchored to the tile it belongs to and grows UPWARD from its
   * top edge (`birdie-board.scss`: `bottom: 100%; margin-bottom: 2px;
   * transform-origin: center bottom`), so on row 0 it lands exactly where the
   * header lives — and it is opaque glass painted after the header (the tiles
   * are later siblings, and `backdrop-filter` gives each one its own stacking
   * context), so without a reserved band it HIDES the board's only text on
   * every round the top row wins.
   *
   * The pill is the SHARED `<WinPill>` and its height is set by its own type,
   * not by the tile it hangs over — so B21's smaller tiles do not shrink it.
   * Measured on the compiled stylesheet in a static 1280x720 harness, with both
   * real Formula faces loaded (the face is what sets the line box, and a
   * fallback face measures ~3 px short), on an exact tile and a count bar
   * alike: at the shared panel's own tier scales a `win` tag reaches 33.5 px
   * above the tile, a `big` payout 41.7 and a `mega` payout 49.3.
   *
   * Thirty-six, not fifty, because the board pins `--tier-scale` to 1
   * (`birdie-board.scss`) — the grid has no room for a 1.4x pill over a 42-px
   * bar — and every tier then measures the same 35.5 px. `boardGeometry.test.ts`
   * asserts row 0 against this number and the review's harness re-measures it,
   * so a retune cannot silently put the pill back over the label; drop the
   * tier pin and this has to go back to 50.
   */
  pillBand: number
  /**
   * How far a tile's DECORATIONS hang outside its own box, REFERENCE px.
   *
   * Two things deliberately hang off a tile corner: the lightning seal at
   * `left: -7px` and the placed-chip pile at `right: -9px`
   * (`birdie-board.scss`). They are painted by the board but they are NOT in
   * any tile box, so a column resolved on tile boxes alone parks them under
   * the icon rail / the social feed, which paint at z 99-150 over the board's
   * z-11 slot. `place()` therefore resolves a block grown by this bleed on the
   * side it is pushed from. (The real hang is 1 px less than the CSS offset —
   * an absolutely positioned child is placed against the PADDING box, inside
   * the tile's 1-px border — so these numbers are a 1-px-conservative model of
   * the measured 6 px / 8 px.)
   */
  tileBleed: {
    /** The lightning seal, off the left edge. */
    left: number
    /** The placed-chip pile, off the right edge. */
    right: number
    /** Both of them, off the top edge — modelled by the tests only. */
    top: number
  }
  /** Existing widget chrome the board must never cover. */
  chrome: {
    socialFeed: ChromeZone
    iconRail: ChromeZone
    topToolbar: ChromeZone
    /**
     * The golfer capsule. Its two keep-outs are DERIVED from the two boxes
     * `cardBoxPx` returns rather than written here as fractions, so the zone
     * and the box it protects cannot drift — the fractions B16b/B21 hand-wrote
     * described the boxes only at the reference frame, and stopped containing
     * them the moment the layout frame grew past it.
     */
    capsule: {
      /**
       * The frame fraction the clip / settled box hangs its RIGHT edge off.
       * The mockup used the frame's own edge; the icon rail owns everything
       * past 93 %, so the capsule stops there instead.
       */
      right: number
      /** What the keep-out adds above and below each box, REFERENCE px. */
      gap: { top: number; bottom: number }
    }
  }
  columns: { count: ColumnSpec; exact: ColumnSpec }
}

export const BIRDIE_BOARD_SPEC: BoardSpec = {
  hole: { x: 0.5, y: 0.86 },
  // Narrowed from 0.4..0.6 (owner, 2026-09-08: "move both boxes closer to the
  // center … still not close to overlapping with the hole"): the putt runs
  // down the middle, and 45..55 % plus the margin keeps 90 px clear of the hole.
  channel: { left: 0.45, right: 0.55 },
  channelMargin: 0.02,
  edgePad: 0.01,
  chipBarTop: 0.91,
  bottomGap: 8,
  ringBand: { left: 0.3, right: 0.7, top: 0.05, bottom: 0.19 },
  holeRadius: 0.03,
  // Two px is enough to keep `intersects` (a strict `<`) off a shared edge
  // without a float-dust tie, and small enough that it never changes which
  // push the resolver picks.
  zoneGap: 2,
  header: { height: 17, gap: 10 },
  pillBand: 0,
  tileBleed: { left: 7, right: 9, top: 9 },
  chrome: {
    socialFeed: { left: 0.01, right: 0.15, top: 0.22, bottom: 0.59 },
    iconRail: { left: 0.93, right: 1, top: 0, bottom: 1 },
    topToolbar: { left: 0, right: 1, top: 0, bottom: 0.06 },
    // B16b: the capsule no longer EXPANDS in place — it has two boxes (the
    // centre-top betting one, owner: "the capsule should maybe be at the center
    // top of the screen during the betting phase", and the top-right clip /
    // settled strip), and each is its own keep-out. The margins are the ones
    // B16b's hand-written fractions worked out to at the reference frame:
    // 2.4 px of air above a box and 2.9 below it, nothing on either side, since
    // both boxes hang off an anchor horizontally.
    capsule: { right: 0.91, gap: { top: 2.4, bottom: 2.9 } }
  },
  columns: {
    /**
     * The right-hand column (B15, kept by B20; sized down by B21). Since B36 it
     * is a 2 x 3 grid of the same 112 x 60 tile the exact column uses, not four
     * 232 x 42 bars: rows 0-1 are the four Any-order options, row 2 is the
     * round-type pair (owner sign-off 2026-09-16 on the placement mockup —
     * "any order looks the same as exact order so we have space for the two new
     * additional special bets below").
     *
     * The arithmetic is why this placement was chosen over a band under the
     * board. Four bars stood 27 + 4x42 + 3x8 = 219 px; two rows of tiles stand
     * 27 + 2x60 + 8 = 155, freeing 64. One more row plus its gap is 68, and
     * because the freed space sits ABOVE the bottom anchor the block lands at
     * 27 + 3x60 + 2x8 = 223 — exactly the exact column's height. So the pair
     * costs no board height, the left column does not move, and no protected
     * zone has to be re-solved. Two columns of 112 + an 8-px gap is still 232,
     * so the block's width and both insets are unchanged as well.
     *
     * The row gap is the settled `<WinPill>`'s own height (`pillBand`), not a
     * decorative gap: the pill is anchored to its tile and grows UPWARD out of
     * it, so anything less puts a winner's payout over the tile above — at B21's
     * 6 px a WINNER tag covered 85 % of its neighbour, including the gold rim,
     * the chip and the price of the option a backer needs to read.
     */
    count: {
      side: 'right',
      gridColumns: 2,
      tileWidth: 112,
      tileHeight: 60,
      columnGap: 8,
      rowGap: 8,
      targetInset: 318,
      bettingInset: 376,
      keys: ['IN0', 'IN1', 'IN2', 'IN3', 'FROST', 'FIRE']
    },
    /**
     * The expert market, alone on the left. Six 112 x 60 tiles on an 8-px
     * column gap, row-major in the order the mockup lists them, so a row is a
     * pair and the three rows read one-holed / mixed / two-holed down the
     * column. The ROW gap is the count column's, and for the same reason: the
     * settled pill grows out of the top of the tile it belongs to.
     *
     * The social feed owns everything left of 15 % between y 22 % and 59 %, and
     * part of the block is inside that band at the reference frame, so the
     * column still takes the feed's x — the seal's 7-px bleed included, which
     * is what puts the tiles at 201 rather than 194. On a picture wide enough
     * that the whole block hangs below the feed, it keeps its 30.7-px inset.
     */
    exact: {
      side: 'left',
      gridColumns: 2,
      tileWidth: 112,
      tileHeight: 60,
      columnGap: 8,
      rowGap: 8,
      targetInset: 318,
      bettingInset: 376,
      keys: ['HMM', 'MHM', 'MMH', 'HHM', 'HMH', 'MHH']
    }
  }
}

/**
 * The one frame the board is ever laid out in (B14). 1280x720 is the stream's
 * native size, so at a full-size desktop stage `k` is 1 and the reference px ARE
 * the screen px. `x`/`y` are 0 because the letterbox offset is not part of the
 * layout any more — it is the position of the scaled box, which
 * `<ArcOverlay>` applies once, to one element.
 */
export const REFERENCE_FRAME: Rect = { x: 0, y: 0, width: 1280, height: 720 }

/**
 * How far apart the width and height scales may drift before the fit is not a
 * contain-fit any more. `houseFrame` returns a contain rect, so the two are
 * equal to floating-point dust in practice; a real divergence would mean a
 * non-16:9 stage, and there the SMALLER scale is the safe one —
 * it keeps the board inside the picture instead of hanging off its long edge.
 */
export const FRAME_SCALE_TOLERANCE = 0.01

/**
 * The single number the whole board is scaled by: the picture box's width over
 * the reference width — the platform's UI-scale model, `useViewportScale`'s
 * `min(innerWidth / 1200, 1)`, CAP INCLUDED.
 *
 * B20 deliberately dropped that cap ("the board is part of the stage, not a
 * page-space panel"). B21 puts it back, because the owner read the result on a
 * wide monitor: "it all looks a bit too large, e.g. the multipliers on the
 * betting options … it's massive compared to how big they usually are … not
 * sure some of it is due to we use a different scaling than what we've
 * generally used". Every other piece of desktop chrome — the chip bar, the
 * capsule's neighbours, the social feed — is fixed px that stops growing at
 * 1200 px of window, so a board that kept growing was the only thing on screen
 * getting bigger. Below the reference frame nothing changes: the board still
 * shrinks with the picture, with no breakpoint.
 *
 * The cap is a SIZE cap, not a registration cap. It is paid for by
 * `layoutFrame` below, which grows the box the layout is computed in by exactly
 * the amount the transform no longer grows — the review of the first B21 build
 * caught the half-state, where the transform was capped and the layout was not,
 * and the board sat in the top-left 1280x720 of every wider picture with its
 * columns 300 px above their own chip bar.
 *
 * Returns 1 for a frame that has not been measured yet (all-zero rect, B2), so
 * a caller that scales something OUTSIDE the overlay — the flying chip — never
 * multiplies it by 0 and loses it.
 */
export function frameScale(frame: Rect): number {
  if (frame.width <= 0 || frame.height <= 0) return 1
  const kx = frame.width / REFERENCE_FRAME.width
  const ky = frame.height / REFERENCE_FRAME.height
  const fit = Math.abs(ky - kx) < FRAME_SCALE_TOLERANCE ? kx : Math.min(kx, ky)
  return Math.min(1, fit)
}

/**
 * The frame the board is LAID OUT in: the picture divided by the scale it will
 * be drawn at, so `layout × frameScale(frame)` is the picture again.
 *
 * Two regimes, one expression, and no breakpoint between them:
 *   - picture SMALLER than the reference — `k` is the honest fit, so this
 *     returns `REFERENCE_FRAME` to floating-point dust and the board is the
 *     reference layout scaled down, exactly as B14 wrote it;
 *   - picture BIGGER — `k` is capped at 1, so this returns the picture itself.
 *     `frameScale` of THAT is 1, which is why every size inside
 *     `boardGeometry` stops at its reference px while every fraction of the
 *     frame (the chip bar, the channel, the icon rail, the social feed, the
 *     capsule's anchors) keeps describing the real picture.
 *
 * A non-16:9 frame takes the smaller fit, so the layout frame is taller or
 * wider than the reference in the other axis and the board still covers the
 * whole picture rather than a 16:9 crop of it. `houseFrame` cannot produce one
 * (B30), but the maths does not depend on that.
 *
 * Returns `REFERENCE_FRAME` for an unmeasured rect: the overlay is not mounted
 * then (`frameIsUsable`), and a zero-sized layout frame would divide by nothing.
 */
export function layoutFrame(frame: Rect): Rect {
  if (frame.width <= 0 || frame.height <= 0) return REFERENCE_FRAME
  const k = frameScale(frame)
  return { x: 0, y: 0, width: frame.width / k, height: frame.height / k }
}

/**
 * Paint order, and the order `boardGeometry` emits tiles in. The easy market
 * first: it is the one a new player reads, and it is the order the arcs used, so
 * the tiles' DOM order (and with it the tab order) is unchanged by B20.
 */
export const COLUMN_ORDER: readonly BoardColumn[] = ['count', 'exact']

export interface TilePlacement {
  key: string
  column: BoardColumn
  /** Row-major index inside the column, matching `ColumnSpec.keys`. */
  index: number
  /** Grid row / grid column inside the block, both 0-based. */
  row: number
  col: number
  left: number
  top: number
  width: number
  height: number
  centreX: number
  centreY: number
}

/**
 * Which side a settled `<WinPill>` hangs off, by the tile's COLUMN INDEX.
 *
 * It is the index and not the family, and that distinction is load-bearing. The
 * rule used to read "the count family always hangs right", which was true while
 * that family was a single column of bars with nothing to its right. B36 made it
 * two tiles wide, and the old rule would have thrown every col-0 pill straight
 * on top of its right-hand neighbour — in the one state where a backer most
 * needs to read the tile underneath.
 *
 * Both families now read the same way: the inner tile's pill goes left into the
 * gap between the columns, the outer tile's goes right into the margin the
 * resolver already keeps clear.
 */
export function pillSideFor(tile: Pick<TilePlacement, 'col'>): 'left' | 'right' {
  return tile.col === 1 ? 'right' : 'left'
}

export interface HeaderPlacement {
  column: BoardColumn
  /** Which end of its own box the label hangs off. */
  align: 'start' | 'end'
  left: number
  top: number
  width: number
  height: number
}

export interface BoardGeometry {
  frame: Rect
  holePx: { x: number; y: number }
  tiles: TilePlacement[]
  headers: HeaderPlacement[]
  /** False when a column had to be dropped below the other to clear its zones. */
  columnsAligned: boolean
  /** True when a column could NOT be placed clear of every protected zone. */
  crowded: boolean
}

const zoneBox = (frame: Rect, zone: ChromeZone): Box => ({
  left: zone.left * frame.width,
  top: zone.top * frame.height,
  width: (zone.right - zone.left) * frame.width,
  height: (zone.bottom - zone.top) * frame.height
})

export function protectedZones(
  frame: Rect,
  spec: BoardSpec = BIRDIE_BOARD_SPEC
): { name: string; box: Box }[] {
  const w = frame.width
  const h = frame.height
  const holeR = spec.holeRadius * h
  return [
    {
      // The ball channel, from the golfer's shoulders down to the hole.
      name: 'channel',
      box: {
        left: spec.channel.left * w,
        top: 0.2 * h,
        width: (spec.channel.right - spec.channel.left) * w,
        height: spec.hole.y * h - 0.2 * h
      }
    },
    {
      name: 'hole',
      box: {
        left: spec.hole.x * w - holeR,
        top: spec.hole.y * h - holeR,
        width: holeR * 2,
        height: holeR * 2
      }
    },
    {
      // Reserved for the engine's burned-in putt rings (Task 4.13).
      name: 'ring-band',
      box: zoneBox(frame, spec.ringBand)
    },
    {
      name: 'chip-bar',
      box: {
        left: 0,
        top: spec.chipBarTop * h,
        width: w,
        height: h - spec.chipBarTop * h
      }
    },
    // Existing widget chrome. None of it moves for the board.
    { name: 'top-toolbar', box: zoneBox(frame, spec.chrome.topToolbar) },
    { name: 'social-feed', box: zoneBox(frame, spec.chrome.socialFeed) },
    { name: 'icon-rail', box: zoneBox(frame, spec.chrome.iconRail) },
    // The capsule's two boxes, each grown into its own keep-out. Derived rather
    // than written down, so the zone follows the box onto any frame.
    { name: 'card', box: capsuleZone(cardBoxPx('condensed', frame, spec), frame, spec) },
    { name: 'capsule-top', box: capsuleZone(cardBoxPx('expanded', frame, spec), frame, spec) }
  ]
}

/** One capsule box, grown by the air the board has to leave around it. */
function capsuleZone(box: CardBox, frame: Rect, spec: BoardSpec): Box {
  const k = frameScale(frame)
  const top = box.top - spec.chrome.capsule.gap.top * k
  const bottom = box.top + box.height + spec.chrome.capsule.gap.bottom * k
  return { left: box.left, top, width: box.width, height: bottom - top }
}

export function intersects(a: Box, b: Box): boolean {
  return (
    a.left < b.left + b.width &&
    b.left < a.left + a.width &&
    a.top < b.top + b.height &&
    b.top < a.top + a.height
  )
}

/**
 * `protectedZones`, with the ball channel widened by `channelMargin` on both
 * sides — the clearance the arcs used to fold into `channelClearanceX`. A
 * column is placed against THESE boxes; `protectedZones` stays the narrower,
 * literal set the invariant tests assert against, so the margin is slack rather
 * than a moved goalpost.
 */
/**
 * Which placement the board is in. `betting` = the closer columns while the
 * round is open (no channel keep-out: nothing rolls yet); `playing` = pushed out
 * to `targetInset` from `race_started` on, clear of the putt's path.
 */
export type BoardLayoutMode = 'betting' | 'playing'

export function columnKeepOuts(
  frame: Rect,
  spec: BoardSpec = BIRDIE_BOARD_SPEC,
  mode: BoardLayoutMode = 'playing'
): { name: string; box: Box }[] {
  const margin = spec.channelMargin * frame.width
  return protectedZones(frame, spec)
    .filter((zone) => mode === 'playing' || zone.name !== 'channel')
    .map((zone) =>
    zone.name === 'channel'
      ? {
          name: zone.name,
          box: {
            left: zone.box.left - margin,
            top: zone.box.top,
            width: zone.box.width + margin * 2,
            height: zone.box.height
          }
        }
      : zone
  )
}

/**
 * How many pushes `resolveColumn` will make before it gives up. Every push is
 * strictly monotonic — the left column's `left` only grows, the right column's
 * only shrinks, and `top` only shrinks — so a column can be pushed off each of
 * the nine zones at most once per axis. Eighteen is the true bound; this is it
 * with slack, and hitting it means `crowded`, not a hang.
 */
const MAX_RESOLVE_PASSES = 24

interface ResolveLimits {
  /** Furthest left a column's left edge may go (the frame's own edge pad). */
  minLeft: number
  /** Furthest right a column's right edge may go. */
  maxRight: number
  /** Furthest UP a column's top edge may go (the frame's own top). */
  minTop: number
}

/**
 * Push one column block off the widget chrome, without ever resizing it.
 *
 * At each pass the FIRST zone the block still overlaps offers two escapes: slide
 * inward past its far edge (right for a left-side column, left for a right-side
 * one), or lift clear above its top. Whichever is the smaller move AND stays
 * inside `limits` wins; if neither fits the block is `blocked` and the board is
 * `crowded` — a state the tests forbid on the reference frame, and the reason
 * this returns a flag instead of throwing.
 *
 * UP, not down (B21). The block is placed bottom-first — its last row sits
 * `bottomGap` above the chip bar — so "further down" is not an escape any more;
 * below the board there is only the bar the player's chips live in.
 *
 * Deterministic on purpose: the zone list is ordered, both candidate moves are
 * closed-form, and ties go to the horizontal move (`dx <= dy`), so the same
 * frame always produces the same board.
 */
export function resolveColumn(
  start: Box,
  side: ColumnSide,
  zones: readonly Box[],
  limits: ResolveLimits,
  gap: number
): { box: Box; blocked: boolean } {
  let box: Box = { ...start }
  for (let pass = 0; pass < MAX_RESOLVE_PASSES; pass++) {
    const hit = zones.find((zone) => intersects(box, zone))
    if (hit === undefined) return { box, blocked: false }

    // Slide inward past the zone. An intersecting zone always has an edge in
    // front of the block on this axis, so `dx` is always strictly positive.
    let dx = Number.POSITIVE_INFINITY
    if (side === 'left') {
      const nextLeft = hit.left + hit.width + gap
      if (nextLeft + box.width <= limits.maxRight) dx = nextLeft - box.left
    } else {
      const nextRight = hit.left - gap
      if (nextRight - box.width >= limits.minLeft) dx = box.left + box.width - nextRight
    }

    // Or lift above it. Same argument the other way up: `hit` starts above
    // `box`'s bottom edge, so `dy > 0`.
    let dy = Number.POSITIVE_INFINITY
    const nextBottom = hit.top - gap
    if (nextBottom - box.height >= limits.minTop) dy = box.top + box.height - nextBottom

    if (!Number.isFinite(dx) && !Number.isFinite(dy)) return { box, blocked: true }
    if (dx <= dy) {
      box = { ...box, left: side === 'left' ? box.left + dx : box.left - dx }
    } else {
      box = { ...box, top: box.top - dy }
    }
  }
  return { box, blocked: zones.some((zone) => intersects(box, zone)) }
}

/**
 * Everything above a column's first tile row: the header line, the mockup's
 * margin under it, and the band the settled `<WinPill>` needs (`pillBand`).
 *
 * ONE function because the two places that need it — the block's height and
 * the tiles' `gridTop` — have to agree exactly; two copies of the same sum is
 * how a header ends up sitting on its own first row.
 */
function headerBlock(spec: BoardSpec, k: number): number {
  return (spec.header.height + spec.header.gap + spec.pillBand) * k
}

/** The block one column occupies: its header line plus its grid of tiles. */
function columnSize(column: ColumnSpec, spec: BoardSpec, k: number): { width: number; height: number } {
  const rows = Math.ceil(column.keys.length / column.gridColumns)
  const width =
    (column.tileWidth * column.gridColumns + column.columnGap * (column.gridColumns - 1)) * k
  const gridHeight = (column.tileHeight * rows + column.rowGap * Math.max(0, rows - 1)) * k
  return { width, height: headerBlock(spec, k) + gridHeight }
}

/**
 * The board's layout for one frame — pass a LAYOUT frame (`boardGeometryFor`).
 *
 * Fixed px (tile sizes, gaps, the header line, the columns' edge insets) are
 * scaled by `frameScale(frame)`; the chrome is read from its own fractions of
 * the frame. Below the reference size those two agree exactly, so every box is
 * the reference box times `k` and the board is SIMILAR at any size — the
 * "sizes scale exactly by k" invariant. At and above it `k` is pinned to 1 and
 * only the fractions grow: the tiles stay the size the owner asked for while
 * the columns keep hanging off the picture's own chip bar, icon rail and social
 * feed. That is the one place the board deliberately stops being similar to
 * itself, and the wide-frame invariants below are what prove it is still
 * registered.
 */
export function boardGeometry(
  frame: Rect,
  spec: BoardSpec = BIRDIE_BOARD_SPEC,
  mode: BoardLayoutMode = 'playing'
): BoardGeometry {
  const k = frameScale(frame)
  const holePx = { x: frame.width * spec.hole.x, y: frame.height * spec.hole.y }
  const zones = columnKeepOuts(frame, spec, mode).map((zone) => zone.box)
  const gap = spec.zoneGap * k
  const limits: ResolveLimits = {
    minLeft: spec.edgePad * frame.width,
    maxRight: frame.width - spec.edgePad * frame.width,
    minTop: 0
  }

  // B21's anchor. Both columns hang off this line with their TOPS aligned, so
  // the taller block's last row lands exactly on it and the shorter one stops
  // short — the two headers stay on one line, which is what makes the board
  // read as one object rather than two lists.
  const sizes: Record<BoardColumn, { width: number; height: number }> = {
    count: columnSize(spec.columns.count, spec, k),
    exact: columnSize(spec.columns.exact, spec, k)
  }
  const anchorBottom = spec.chipBarTop * frame.height - spec.bottomGap * k
  const startTop = anchorBottom - Math.max(sizes.count.height, sizes.exact.height)

  /**
   * A resolved column: the block the tiles are laid out in, and the box the
   * RESOLVER tested — the block grown by the decoration bleed on the side it
   * is pushed from (`tileBleed`). The two differ by a few px and the
   * difference is the whole point: `box` is what the board draws, `hitBox` is
   * what must clear the chrome.
   */
  interface PlacedColumn {
    column: ColumnSpec
    box: Box
    hitBox: Box
    blocked: boolean
  }

  const place = (name: BoardColumn): PlacedColumn => {
    const column = spec.columns[name]
    const size = sizes[name]
    // OUTWARD side only. The chrome a column is pushed off is on the side it
    // hangs from — the social feed for the left column, the icon rail for the
    // right one — and that is the edge whose decoration would be painted over.
    // Growing BOTH sides is what does not fit: the exact column's corridor
    // between the feed and the widened channel is 292.4 px against a 281.6-px
    // block, so a 16-px inflation would fail to fit and the resolver would
    // slide the column across the channel into the count column. The inward
    // bleed lands in the channel's own 25.6-px margin instead — slack the
    // widened keep-out already holds for exactly this kind of overhang — and
    // `boardGeometry.test.ts` proves it against the LITERAL channel.
    const bleed = (column.side === 'left' ? spec.tileBleed.left : spec.tileBleed.right) * k
    const inset = (mode === 'betting' ? column.bettingInset : column.targetInset) * k
    const left = column.side === 'left' ? inset : frame.width - inset - size.width
    const start: Box = {
      left: column.side === 'left' ? left - bleed : left,
      top: startTop,
      width: size.width + bleed,
      height: size.height
    }
    const resolved = resolveColumn(start, column.side, zones, limits, gap)
    const box: Box = {
      left: column.side === 'left' ? resolved.box.left + bleed : resolved.box.left,
      top: resolved.box.top,
      width: size.width,
      height: size.height
    }
    return { column, box, hitBox: resolved.box, blocked: resolved.blocked }
  }

  const placed: Record<BoardColumn, PlacedColumn> = {
    count: place('count'),
    exact: place('exact')
  }
  const crowded = COLUMN_ORDER.some((name) => placed[name].blocked)

  // One row of headers reads as one board. Both columns take the HIGHER of the
  // two resolved tops whenever that still clears every zone for both of them —
  // the resolver only ever lifts, so the aligned top is the highest one, and
  // the only thing that can refuse is chrome above it (or the frame's own top).
  const alignedTop = Math.min(...COLUMN_ORDER.map((name) => placed[name].box.top))
  const columnsAligned = COLUMN_ORDER.every((name) => {
    // The BLED box again: aligning is a move, and a move has to clear the
    // chrome by the same rule the resolver used.
    const lifted: Box = { ...placed[name].hitBox, top: alignedTop }
    return lifted.top >= limits.minTop && !zones.some((zone) => intersects(lifted, zone))
  })
  const boxOf = (name: BoardColumn): Box =>
    columnsAligned ? { ...placed[name].box, top: alignedTop } : placed[name].box

  const tiles: TilePlacement[] = []
  const headers: HeaderPlacement[] = []
  for (const name of COLUMN_ORDER) {
    const column = placed[name].column
    const box = boxOf(name)
    headers.push({
      column: name,
      align: column.side === 'left' ? 'start' : 'end',
      left: box.left,
      top: box.top,
      width: box.width,
      height: spec.header.height * k
    })

    const gridTop = box.top + headerBlock(spec, k)
    const width = column.tileWidth * k
    const height = column.tileHeight * k
    for (let index = 0; index < column.keys.length; index++) {
      const key = column.keys[index]
      if (key === undefined) continue
      const row = Math.floor(index / column.gridColumns)
      const col = index % column.gridColumns
      const left = box.left + col * (width + column.columnGap * k)
      const top = gridTop + row * (height + column.rowGap * k)
      tiles.push({
        key,
        column: name,
        index,
        row,
        col,
        left,
        top,
        width,
        height,
        centreX: left + width / 2,
        centreY: top + height / 2
      })
    }
  }

  return {
    frame,
    holePx,
    tiles,
    headers,
    columnsAligned,
    crowded
  }
}

/** The golfer capsule's box, in REFERENCE px like every other export here. */
export interface CardBox {
  left: number
  top: number
  width: number
  /**
   * FIXED per state, never a cap (B12, B16, B16b). The capsule's contents never
   * change count WITHIN a state, so its height is a constant the host sets on
   * the element and nothing inside can make it breathe. B16b gives the two
   * boxes different `left`/`top`/`width` as well, so the move and the reshape
   * are the same four transitioned properties.
   */
  height: number
}

/**
 * The capsule's two boxes, in REFERENCE px (B16b), every one of them multiplied
 * by 0.85 for B21.
 *
 * Owner on the live board: "also i guess for the capsule, it does seem a bit
 * too large". So the two boxes and EVERY dimension inside them (`.birdie-card`
 * in `birdie-board.scss`) are the B16b numbers times 0.85 — one factor, applied
 * once, so the capsule is the same drawing at 85 % rather than a re-tuned one.
 * The two ANCHORS are kept: the betting box still hangs 58.88 px off the top
 * (centred on the frame) and the clip box still hangs off `chrome.capsule.right`
 * at 96 px.
 *
 * SIZES, not fractions of the frame (the review of the first B21 build): the
 * capsule shrinks with a small picture and stops at its reference size on a big
 * one, exactly like the tiles, because it is the same `k`. A width kept as a
 * fraction would have gone back to 471 px on a 1846-wide picture with its own
 * fixed-px contents rattling around inside it — the "too large" the owner
 * reported, in the one place B21 was meant to have fixed it.
 *
 * The mockup expresses every size as `cqw` of a 1280-wide frame, so 1 cqw =
 * 12.8 reference px; each constant below records the mockup value it came from.
 * The two HEIGHTS are the measured content heights of the mockup's two states
 * at a 1280-wide frame — the capsule is still never sized by its own contents
 * (B11 item 1: a stream-module `img` rule once blew the plaque up to 49 % of the
 * frame), it is pinned to the number its contents were designed to need.
 */

/** `width: 30cqw` × 0.85. 1280 × 0.3 × 0.85 = 326.4 px, centred on the frame. */
export const CARD_CAPSULE_BETTING_WIDTH_PX = 326.4
/** `top: 4.6cqw`. The ANCHOR is unscaled — only the box it holds shrank. */
export const CARD_CAPSULE_BETTING_TOP_PX = 58.88
/** (Head 48.1 + stats 46.1 + total 58.7 + padding 26.9 + two 9.6 gaps) × 0.85. */
export const CARD_CAPSULE_BETTING_HEIGHT_PX = 170.85

/**
 * The clip / settled strip: `width: 27cqw` × 0.85 = 293.76 px, hung off
 * `chrome.capsule.right`. The mockup anchors it to the frame edge
 * (`100% − 6.5cqw`); on the real board the icon rail owns everything past 93 %,
 * so it hangs off 91 % instead — 25.6 px clear of the rail rather than 6.4 px
 * under it.
 */
export const CARD_CAPSULE_CLIP_WIDTH_PX = 293.76
/**
 * `top`. NOT the mockup's 13.5cqw (172.8 px): a capsule from there ends 130 px
 * lower than the strip the count column is resolved around, so a capsule
 * hanging out of its own keep-out is a capsule over the board. Lifted to 96 px,
 * which ends the strip at 206.5, with the whole thing still below the top
 * toolbar (43.2 px). The one place the mockup could not be carried over 1:1.
 */
export const CARD_CAPSULE_CLIP_TOP_PX = 96
/** (Head 48.1 + total 48.4 + padding 21.8 + one 9.6 gap) × 0.85. */
export const CARD_CAPSULE_CLIP_HEIGHT_PX = 110.5

/**
 * The golfer capsule's box in REFERENCE px — ONE of two, chosen by the capsule
 * layout (`capsuleState`, whose `CapsuleLayout` this signature is typed
 * against so the two can never drift).
 *
 * B16b, owner: "the capsule should maybe be at the center top of the screen
 * during the betting phase, and when betting is over it moves to the top right
 * like now". So `expanded` returns the centre-top box and the other two return
 * the top-right one, and `<BirdieCard>` — which carries no coordinates of its
 * own — transitions between them as four inline properties. Everything here is
 * frame-LOCAL (B14: the letterbox offset belongs to the scaled box, which
 * `<ArcOverlay>` applies once, to one element).
 *
 * Each box has a keep-out of its own — `protectedZones`' `capsule-top` and
 * `card`, grown from these very boxes — so no state of the capsule can reach a
 * tile whatever the columns are retuned to; `boardGeometry.test.ts` asserts the
 * clearance rather than the coordinates, so the boxes can be nudged freely.
 *
 * Sizes are the reference px above times `frameScale(frame)` and the two
 * positions are anchored to the frame (one centred, one hung off
 * `chrome.capsule.right`), which is the same rule every tile follows: the box
 * shrinks with a small picture, stops at its reference size on a big one, and
 * stays where it belongs on the picture either way.
 */
export function cardBoxPx(
  layout: CapsuleLayout = 'expanded',
  frame: Rect = REFERENCE_FRAME,
  spec: BoardSpec = BIRDIE_BOARD_SPEC
): CardBox {
  const k = frameScale(frame)
  if (layout === 'expanded') {
    const width = CARD_CAPSULE_BETTING_WIDTH_PX * k
    return {
      left: (frame.width - width) / 2,
      top: CARD_CAPSULE_BETTING_TOP_PX * k,
      width,
      height: CARD_CAPSULE_BETTING_HEIGHT_PX * k
    }
  }
  const width = CARD_CAPSULE_CLIP_WIDTH_PX * k
  return {
    left: frame.width * spec.chrome.capsule.right - width,
    top: CARD_CAPSULE_CLIP_TOP_PX * k,
    width,
    height: CARD_CAPSULE_CLIP_HEIGHT_PX * k
  }
}

/**
 * The board's ONE layout (B14), computed at most once per page load.
 *
 * A module-level memo rather than a `useMemo`: the geometry is a pure function
 * of two module constants, so every caller — the desktop overlay, the host, a
 * future rules panel — must get the same object, and a re-mount must not pay for
 * it again. `boardGeometry` itself stays frame-parameterised for the tests,
 * which still exercise it on letterboxed and oversized rects.
 */
let referenceGeometry: BoardGeometry | undefined
export function referenceBoardGeometry(): BoardGeometry {
  if (referenceGeometry === undefined) referenceGeometry = boardGeometry(REFERENCE_FRAME)
  return referenceGeometry
}

/**
 * The board's layout for a PICTURE BOX — the one production caller.
 *
 * `layoutFrame` collapses every picture at or below the reference size onto
 * `REFERENCE_FRAME` itself, so on those the answer is the reference layout and
 * this is `referenceBoardGeometry()` under another name; above it the layout is
 * the picture, and the columns keep hanging off the real chip bar and the real
 * icon rail instead of stopping in a 1280x720 corner of it.
 *
 * A one-entry cache on the SIZE rather than on the rect's identity:
 * `useStageFrame` hands out a fresh object whenever the letterbox offset moves,
 * and a pan of the picture inside its container does not change a single number
 * here.
 */
const lastLayout: Partial<Record<BoardLayoutMode, Rect>> = {}
const lastGeometry: Partial<Record<BoardLayoutMode, BoardGeometry>> = {}

export function boardGeometryFor(frame: Rect, mode: BoardLayoutMode = 'playing'): BoardGeometry {
  const layout = layoutFrame(frame)
  const cachedLayout = lastLayout[mode]
  const cached = lastGeometry[mode]
  if (
    cached !== undefined &&
    cachedLayout !== undefined &&
    cachedLayout.width === layout.width &&
    cachedLayout.height === layout.height
  ) {
    return cached
  }
  lastLayout[mode] = layout
  const geometry = boardGeometry(layout, BIRDIE_BOARD_SPEC, mode)
  lastGeometry[mode] = geometry
  return geometry
}
